import { useEffect, useState } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { BelayUserConnection, UserConnectionStatus, UserConnectionListKey } from '@alltrails/shared/types/belay';
import { Context } from '@alltrails/shared/types/context';
import { getConnections } from '@alltrails/shared/utils/requests/belayRequests';
import logFollowersViewed from '@alltrails/analytics/events/logFollowersViewed';
import logFollowingViewed from '@alltrails/analytics/events/logFollowingViewed';

import CustomProvider from 'components/CustomProvider';
import BelayUserConnections from './BelayUserConnections';

type Props = {
  currentUserId: number;
  context: Context;
};

type Connection = {
  data: BelayUserConnection[];
  totalItemCount: number;
};

type Connections = {
  'followed-by': Connection;
  following: Connection;
  mutual: Connection;
};

const AvatarStackContainer = ({ currentUserId, context }: Props): JSX.Element => {
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [showMutualModal, setShowMutualModal] = useState(false);
  const [connections, setConnections] = useState<Connections>();

  useEffect(() => {
    // Once we have our pages wrapped in redux providers, we can use useUserConnections from the UserConnections module
    const fetchConnections = async (status: UserConnectionStatus) => {
      const data = await getConnections(status, currentUserId as number);
      setConnections(prevState => ({
        ...prevState,
        [status]: {
          data: data?.connections,
          // permanent solution because mutual request is not paginated
          totalItemCount: status === 'mutual' ? data?.pageInfo?.itemCount : data?.pageInfo?.totalItemCount
        }
      }));
    };

    fetchConnections('followed-by' as UserConnectionStatus).catch(console.error);
    fetchConnections('following' as UserConnectionStatus).catch(console.error);
    if (context.currentUser.id !== currentUserId) {
      fetchConnections('mutual' as UserConnectionStatus).catch(console.error);
    }
  }, []);

  const followers = connections?.['followed-by'];
  const following = connections?.following;
  const mutual = connections?.mutual;

  const isOwnedByUser = context?.currentUser?.id === currentUserId;
  const amplitudeEventObject = { owned_by_user: isOwnedByUser, owner_id: `${currentUserId}` };

  return (
    <CustomProvider>
      {followers?.totalItemCount > 0 && (
        <BelayUserConnections
          title={<FormattedMessage defaultMessage="Followers" />}
          connections={followers?.data}
          count={followers?.totalItemCount}
          onOverflowClick={() => {
            logFollowersViewed(amplitudeEventObject);
            setShowFollowersModal(true);
          }}
          listKey={UserConnectionListKey.Followers}
          context={context}
          showModal={showFollowersModal}
          setShowModal={setShowFollowersModal}
          userId={currentUserId}
        />
      )}
      {following?.totalItemCount > 0 && (
        <BelayUserConnections
          title={<FormattedMessage defaultMessage="Following" />}
          connections={following?.data}
          count={following?.totalItemCount}
          listKey={UserConnectionListKey.Following}
          context={context}
          onOverflowClick={() => {
            logFollowingViewed(amplitudeEventObject);
            setShowFollowingModal(true);
          }}
          showModal={showFollowingModal}
          setShowModal={setShowFollowingModal}
          userId={currentUserId}
        />
      )}
      {mutual?.totalItemCount > 0 && (
        <BelayUserConnections
          title={<FormattedMessage defaultMessage="Mutual" />}
          connections={mutual?.data}
          count={mutual?.totalItemCount}
          listKey={UserConnectionListKey.Mutual}
          context={context}
          onOverflowClick={() => setShowMutualModal(true)}
          showModal={showMutualModal}
          setShowModal={setShowMutualModal}
          userId={currentUserId}
        />
      )}
    </CustomProvider>
  );
};

export default AvatarStackContainer;
